import React, { useContext, useEffect, useRef, useState } from 'react';
import closeImg from './assets/images/close_1close.png';
import externalImg from './assets/images/external-link.svg';
import { AccountProvider } from './context';
import './assets/css/normalize.css';
import './assets/css/webflow.css';
import './assets/css/ibl-default-account-ui.webflow.css';
import './assets/css/custom.css';
import { Tab } from './layouts';
import Basic from './layouts/tab-contents/Basic';
import Social from './layouts/tab-contents/Social';
import Security from './layouts/tab-contents/Security';
import Header from './layouts/Header';
import { api, getTenant, getUserMetadataAPI, getUserName } from '../../hooks';
import { AppDataContext } from '../../utils/context';
import { IUserData, IUserMetaData } from '../../model';
import { useDebounce } from './helpers/useDebounce';
import Users from './layouts/tab-contents/Users';
import { isJSON } from '../../utils';
import { all } from 'axios';
import { useTranslation } from 'react-i18next';

const TABS = [
  {
    slug: 'Basic',
    name: 'Basic',
    layout: <Basic />,
  },
  {
    slug: 'Social',
    name: 'Social',
    layout: <Social />,
  },
  {
    slug: 'Security',
    name: 'Security',
    layout: <Security />,
  },
  {
    slug: 'Admin',
    name: 'Admin',
    layout: <Users />,
  },
];

const STRIPE_ENABLED = process.env.REACT_APP_IBL_ENABLE_STRIPE === 'true';

export const Account = ({ onModalClose, darkMode = false }) => {
  const [focusedTab, setFocusedTab] = useState('Basic');
  const [initialUserEmail, setInitialUserEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { appData, setAppData } = useContext(AppDataContext);
  const { t } = useTranslation('library');
  const [userData, setUserData] = useState<IUserData>({ ...appData.userData });
  const [userMetaData, setUserMetaData] = useState<IUserMetaData>({
    ...appData.userMetaData,
  });
  const [userProfilePicUpdated, setUserProfilePicUpdated] = useState(false);
  const [stripeBillingURL, setStripeBillingURL] = useState('');
  const [isTenantAdmin, setIsTenantAdmin] = useState(false);
  const [notification, setNotification] = useState(null);
  const modalRef = useRef(null);

  const handleNotification = (notifData, autoClose = 5000) => {
    setNotification(notifData);
    setTimeout(() => {
      setNotification({});
    }, autoClose);
  };

  const handleRetrieveBillingURL = () => {
    if (!STRIPE_ENABLED) {
      return;
    }
    const url = new URL(window.location.href);
    const urlOrigin = url.origin;
    const data = {
      return_url: urlOrigin,
      //callback: 'createCustomerPortalURL',
    };
    stripeBilling(data, (url) => {
      if (url && String(url) !== '') {
        setStripeBillingURL(url);
      }
    });
  };

  const stripeBilling = async (data, callback) => {
    /*api.iblwebstripe.postToStripe(data, function (url) {
      callback(url);
    });*/
    const tenant = getTenant();
    const token = localStorage.getItem('axd_token');
    const url = `${process.env.REACT_APP_IBL_DM_URL}/api/service/orgs/${tenant}/users/${appData.userData.user_nicename}/stripe/customer-portal/`;
    await fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token,
      },
    })
      .then((resp) => {
        resp
          .json()
          .then((data) => {
            if (data?.url) {
              callback(data.url);
            }
          })
          .catch((err) => {
            //
          });
      })
      .catch((err) => {
        //
      });
  };

  const handleBillingClick = () => {
    if (stripeBillingURL && String(stripeBillingURL) !== '') {
      window.location.href = stripeBillingURL;
    }
  };

  const handleCheckIfTenantAdmin = () => {
    const tenant = getTenant();
    const allTenants = isJSON(localStorage.getItem('tenants'))
      ? JSON.parse(localStorage.getItem('tenants'))
      : [];
    setIsTenantAdmin(!!allTenants?.find((pre) => pre.key === tenant)?.is_admin);
  };

  useEffect(() => {
    handleUserData();
    handleRetrieveBillingURL();
    handleCheckIfTenantAdmin();
  }, []);
  const METADATANAMES = ['bio', 'about', 'is_profile_public', 'name', 'email', 'language'];

  const userCanStoreProfilePic = () => {
    const actualYearOfBirth = appData?.userMetaData?.year_of_birth;
    if (!actualYearOfBirth) {
      return false;
    }
    try {
      return new Date().getFullYear() - actualYearOfBirth >= 13;
      //ACCEPTABLE AGE LIMIT FOR EDX TO STORE PICTURE PIC
    } catch {
      return false;
    }
  };

  function getCookie(name: string) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        // Does this cookie string begin with the name we want?
        if (cookie.substring(0, name.length + 1) === name + '=') {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  // TODO : Call these endpoints from the ibl web core front end instead of making a direct call
  const handleStoreProfilePic = (formData: any) => {
    const username = appData?.userMetaData?.username;
    const learnURL = process.env.REACT_APP_IBL_LMS_URL;
    fetch(`${learnURL}/api/user/v1/accounts/${username}/image`, {
      method: 'POST',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
      },
      body: formData,
      redirect: 'follow',
      credentials: 'include', // This line is added to include credentials like cookies
    })
      .then((response) => response.text())
      .then((result) => {
        fetch(`${learnURL}/api/user/v1/accounts/${username}`, {
          method: 'GET',
          credentials: 'include', // This line is added to include credentials like cookies
        })
          .then((response) => response.json())
          .then((fetchedUserData: any) => {
            const updatedMetaData = {
              ...userMetaData,
              public_metadata: {
                ...userMetaData?.public_metadata,
                profile_image: fetchedUserData?.profile_image,
              },
              profile_image: fetchedUserData?.profile_image,
            };
            setUserMetaData(updatedMetaData);
            setAppData({
              ...appData,
              userMetaData: { ...updatedMetaData },
            });
            setUserProfilePicUpdated(true);
          });

        /*api.ibledxusers.getUserAccountDetails({
            username:appData?.userMetaData?.username,
          }, (fetchedUserData)=>{
            const updatedMetaData = {
              ...userMetaData,
              public_metadata: { ...appData['public_metadata'], profile_image:fetchedUserData?.profile_image?.image_url_full },
            }
            setUserMetaData(updatedMetaData);
            setAppData({
              ...appData,
              userMetaData: { ...updatedMetaData },
            });
          })*/
      })
      .catch((error) => console.error(error));
  };

  const handleImgUploadToAPI = (formdata: any) => {
    setIsSubmitting(true);
    const userCanStore = userCanStoreProfilePic();
    if (userCanStore) {
      handleStoreProfilePic(formdata);
    } else {
      handleUpdateUserDateOfBirth()
        .then((response) => {
          handleStoreProfilePic(formdata);
        })
        .catch((err) => {
          //
        });
    }
  };

  const handleUpdateUserDateOfBirth = async () => {
    const username = appData?.userMetaData?.username;
    const learnURL = process.env.REACT_APP_IBL_LMS_URL;
    const apiUrl = `${learnURL}/api/user/v1/accounts/${username}`;
    const DEFAULT_YEAR_OF_BIRTH = '1996';

    const payload = {
      year_of_birth: DEFAULT_YEAR_OF_BIRTH,
    };

    return fetch(apiUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/merge-patch+json',
      },
      body: JSON.stringify(payload),
      credentials: 'include',
    }).then((response) => response.json());
  };

  const handleSubmit = () => {
    handleMetaDataSubmitToAPI();
  };

  const handleMetaDataSubmitToAPI = () => {
    const { bio, about, is_profile_public, social_links, profile_image, language } =
      userMetaData;
    const emailGotUpdated =
      userMetaData?.email !== initialUserEmail && userMetaData?.email !== '';
    const updatedDataWithoutProfilePic = {
      name: userMetaData?.name || '',
      bio: bio || '',
      about: about || '',
      is_profile_public,
      language: language ?? "en",
      social_links,
      ...(!!emailGotUpdated
        ? {
            email: userMetaData.email,
          }
        : {}),
    };
    const metaDataToSubmit = {
      public_metadata: {
        ...updatedDataWithoutProfilePic,
        profile_image,
      },
      ...updatedDataWithoutProfilePic,
      username: appData.userData.user_nicename,
    };
    setIsSubmitting(true);
    api.ibledxusers.postUsersManageMetadata(
      JSON.stringify(metaDataToSubmit),
      function (response) {
        setIsSubmitting(false);
        handleNotification({
          success: true,
          message: `Profile updated successfully.${
            emailGotUpdated
              ? ' An email with a link has been sent to your new email address.'
              : ''
          }`,
        });
        setUserProfilePicUpdated(false);
        setAppData({
          ...appData,
          userMetaData: { ...userMetaData },
          userData: {
            ...userData,
            user_fullname: userMetaData?.name,
            ...(!!emailGotUpdated
              ? {
                  user_email: userMetaData?.email,
                }
              : {}),
          },
        });
        setInitialUserEmail(userMetaData?.email);
      },
      function (error) {
        setIsSubmitting(false);
        setUserProfilePicUpdated(false);
        handleNotification({
          error: true,
          message: 'Profile update failed. Please try again!',
        });
        console.error(error);
      }
    );
  };

  //TRIGGERS SAVE TO METADATA WHENEVER PROFILE PIC UPDATED
  useDebounce(
    () => {
      if (userProfilePicUpdated) {
        handleMetaDataSubmitToAPI();
      }
    },
    300,
    [userProfilePicUpdated]
  );

  const handlePassWordForgotten = (callback, errCallback) => {
    const email = userData.user_email;
    api.iblwebauth.forgetPassword(
      {
        email: email,
      },
      callback,
      errCallback
    );
  };

  const handleModalCloserClick = () => {
    onModalClose(false);
  };

  const handleUserData = () => {
    if (!userMetaData.name) {
      getUserMetadataAPI(getUserName(), (updatedUserMetaData) => {
        setUserMetaData({ ...updatedUserMetaData });
        setAppData({
          ...appData,
          userMetaData: { ...updatedUserMetaData },
          ibl_user_public_metadata: { ...updatedUserMetaData },
        });
      });
    }
    setInitialUserEmail(userData?.user_email);
  };

  //MODAL FOCUS TRAP HERE
  useDebounce(
    () => {
      const modalElement = modalRef.current;
      //add any focusable HTML element you want to include to this string
      const focusableElements = modalElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKeyPress = (event) => {
        if (event.key === 'Tab') {
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      };

      modalElement.addEventListener('keydown', handleTabKeyPress);

      return () => {
        modalElement.removeEventListener('keydown', handleTabKeyPress);
      };
    },
    200,
    [focusedTab]
  );

  return (
    <AccountProvider
      value={{
        focusedTab,
        setFocusedTab,
        onUserDataChange: (_userData: IUserData) =>
          setUserData({ ..._userData }),
        userData: { ...userData },
        userMetaData: { ...userMetaData },
        onUserMetaDataChange: (_userMetaData: IUserMetaData) =>
          setUserMetaData({ ..._userMetaData }),
        METADATANAMES,
        handleSubmit,
        handlePassWordForgotten,
        handleImgUploadToAPI,
        isSubmitting,
        stripeBillingURL,
        handleBillingClick,
        darkMode,
        notification,
      }}
    >
      <div
        className={`modal manage-account-modal ${
          stripeBillingURL !== '' ? '' : 'no-billing'
        } ${darkMode ? 'dark-mode-active' : ''}`}
        style={{
          display: 'flex',
        }}
        aria-modal={true}
        ref={modalRef}
      >
        <div className="w-layout-vflex-account-ui modal-container export-modal-container larger">
          <div className="w-layout-vflex-account-ui modal-body manage-account-modal-body">
            <div className="w-layout-vflex-account-ui account-user-details-block">
              <Header />
              <div className="w-layout-hflex-account-ui account-seperator"></div>
              <div
                className={`w-layout-grid-account-ui account-grid-block ${
                  stripeBillingURL !== '' ? 'with-billing' : ''
                } ${isTenantAdmin ? 'with-admin' : ''}`}
              >
                <Tab slug={'Basic'} name={t('Basic')} />
                <Tab slug={'Social'} name={t('Social')} />
                <Tab slug={'Security'} name={t('Security')} />
                {stripeBillingURL !== '' && (
                  <Tab
                    slug={'Billing'}
                    name={'Billing'}
                    url={stripeBillingURL}
                  />
                )}
                <Tab
                  slug={'Admin'}
                  name={'Admin'}
                  isTenantAdmin={isTenantAdmin}
                />
              </div>
              <div
                onClick={() => handleModalCloserClick()}
                className="modal-close-wrapper hidden-sm"
              >
                <img src={closeImg} loading="lazy" alt="" />
              </div>
            </div>
            <div className="w-layout-hflex-account-ui account-user-form-block">
              <div className="w-layout-hflex-account-ui modal-header">
                <h3 className="modal-header-title">
                  {TABS.find((el) => el.slug === focusedTab).name}
                </h3>
                {stripeBillingURL !== '' && (
                  <div
                    onClick={() => handleBillingClick()}
                    className="w-layout-hflex-account-ui billing-btn-block"
                  >
                    <h5 className="billing-btn-header">{t('Billing')}</h5>
                    <img
                      src={externalImg}
                      loading="lazy"
                      alt=""
                      className="billing-btn-icon invert"
                    />
                  </div>
                )}
                <div
                  onClick={() => handleModalCloserClick()}
                  className="modal-close-wrapper"
                >
                  <img src={closeImg} loading="lazy" alt="" />
                </div>
              </div>
              {TABS.find((el) => el.slug === focusedTab).layout}
            </div>
          </div>
        </div>
      </div>
    </AccountProvider>
  );
};
